import React, { useRef, useState } from 'react';
import { FactureOpenSearch, useFacturationService } from '@europrocurement/l2d-domain';
import { MenuItem, Typography } from '@mui/material';

import { AxiosError } from 'axios';
import { enqueueSnackbar } from 'notistack';
import { useDispatch } from 'react-redux';
import { AnyAction } from '@reduxjs/toolkit';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import { FactureApiApiFacturesIdAvoirPutRequest } from '@europrocurement/l2d-domain/openApi/ApiFacturation';
import { ConfirmWarningModal } from '@europrocurement/flexy-components';
import { FactureType } from '../../../hooks';

export type menuGenerateCreditNoteType = {
    facture: FactureOpenSearch;
    xIdSociete: number;
};
const MenuGenerateCreditNote: React.FunctionComponent<menuGenerateCreditNoteType> = function ({
    facture,
    xIdSociete,
}) {
    const isSubmittedCreditNote = useRef(false);
    const dispatch = useDispatch();
    const { getFactureOpenSearch, factureApi } = useFacturationService();
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [submitingProcess, setSubmitingProcess] = useState<boolean>(false);

    const handleCloseModal = () => {
        setSubmitingProcess(false);
        setIsModalOpen(false);
    };

    const generateAvoirRequest = (factureId: number): Promise<FactureType> =>
        new Promise((resolve, reject) => {
            const requestParameter: FactureApiApiFacturesIdAvoirPutRequest = {
                id: factureId.toString() as string,
                xIdSociete,
                factureJsonld: {
                    '@id': `/facturation/factures/${factureId}`,
                    '@type': 'Facture',
                },
            };

            factureApi
                .apiFacturesIdAvoirPut(requestParameter)
                .then((res) =>
                    resolve({
                        id: res.data?.id as number,
                    }),
                )
                .catch((error) => {
                    if (
                        error instanceof AxiosError &&
                        error.response &&
                        error.response.data.exception &&
                        error.response.data.exception.message
                    ) {
                        enqueueSnackbar(
                            <Typography>{error.response.data.exception.message}</Typography>,
                            {
                                variant: 'error',
                            },
                        );
                    } else {
                        enqueueSnackbar(
                            <Typography>
                                Une erreur est survenue lors de la création d&apos;un avoir
                            </Typography>,
                            {
                                variant: 'error',
                            },
                        );
                    }
                    reject(
                        new Error('Une erreur est survenue lors de la création d&apos;un avoir'),
                    );
                });
        });

    const handleGenerateCreditNoteClick = () => {
        setIsModalOpen(true);
    };

    return (
        <>
            <MenuItem
                sx={{ gap: '4px' }}
                onClick={handleGenerateCreditNoteClick}
            >
                <FaOptionIcon
                    icon={['fasl', 'receipt']}
                    color="text.gris70"
                />
                <Typography sx={{ color: 'text.primary' }}>Générer un avoir</Typography>
            </MenuItem>

            <ConfirmWarningModal
                actionOnValidation={() => {
                    if (facture.id) {
                        setSubmitingProcess(true);
                        // Prevent user multiple clicks
                        if (isSubmittedCreditNote.current === true) {
                            return;
                        }
                        isSubmittedCreditNote.current = true;

                        generateAvoirRequest(facture.id).finally(() => {
                            isSubmittedCreditNote.current = false;
                            dispatch(getFactureOpenSearch({}) as unknown as AnyAction);
                            handleCloseModal();
                        });
                    }
                }}
                handleCloseModal={handleCloseModal}
                modalOpen={isModalOpen}
                submitingProcess={submitingProcess}
                title={`Souhaitez-vous générer un avoir pour la facture ${facture.factureNumero} ?`}
                message={`Génération d'avoir pour la facture ${facture.factureNumero} du prescripteur ${facture.prescripteurLibelle}`}
                warningLevel="primary"
                validationButtonText="Oui, générer"
            />
        </>
    );
};

export default MenuGenerateCreditNote;
